import { currencySum } from 'utils/generic';
import { GroupsFilterableGrid } from '../GroupsFilterableGrid';
import { AccountsFilterableGrid } from '../AccountsFilterableGrid';
import { v4 as uuid } from 'uuid';
import { JournalEntryForm } from '../JournalEntryForm';
import { JournalStatus } from 'constants/types';
import currency from 'currency.js';
import React from 'react';
import { connect } from 'react-redux';
import { journalEntriesWithItemsSelector } from 'selectors/journals';
import { periodsSelector } from 'selectors/periods';
import { settingsSelector } from 'selectors/settings';
import { JournalLabel } from 'constants/labels';
import { addUpdateJournalEntry } from 'actions/journalActions';
import { openModal } from 'actions/modalActions';
import { exchangeRateChoicesSelector } from 'selectors/exchangeRateRows';

class JournalEntryModal extends React.PureComponent {
  constructor(props) {
    super(props);

    this.state = props.journalEntry
      ? {
          ...props.journalEntry,
          showGroups: true,
        }
      : {
          id: -1,
          periodId: props.periods[0]?.id,
          type: props.data.type,
          description: '',
          status: JournalStatus.unposted,
          modified: new Date(),
          items: {},
          showGroups: true,
          er: null,
        };
  }

  componentDidUpdate(prevProps, prevState) {
    const isPosted = this.state.status === JournalStatus.posted;
    const itemsChanged = prevState.items !== this.state.items;
    if (isPosted && itemsChanged && !this.canBePosted) {
      this.setState({ status: JournalStatus.unposted });
    }
  }

  get isRecord() {
    return this.state.type === 'aje';
  }

  get creditSum() {
    return currencySum(Object.values(this.state.items).map((i) => i.credit));
  }

  get debitSum() {
    return currencySum(Object.values(this.state.items).map((i) => i.debit));
  }

  get total() {
    return this.debitSum.subtract(this.creditSum);
  }

  get canBePosted() {
    return (
      (this.total.intValue === 0 &&
        this.debitSum.intValue !== 0 &&
        this.creditSum.intValue !== 0) ||
      (this.isRecord && !!this.state.er)
    );
  }

  handleUpdate = (update) => {
    if (update.status && !this.canBePosted) {
      update.status = JournalStatus.unposted;
    }

    this.setState(update);
  };

  showConfirmation = (text, onConfirm) => {
    this.props.openModal({
      id: uuid(),
      type: 'confirmation',
      text,
      onConfirm,
    });
  };

  addEntryItem = (contentType, contentId, contentDescription) => {
    if (contentId < 0) {
      // id = -1 for Unassgined accounts virtual group
      return;
    }

    let newId = -1;
    while (this.state.items.hasOwnProperty(newId)) {
      newId -= 1;
    }
    this.handleUpdate({
      items: {
        ...this.state.items,
        [newId]: {
          entryId: this.state.id,
          contentType,
          contentId,
          contentDescription,
          debit:
            this.total.intValue < 0 ? this.total.multiply(-1) : currency(0),
          credit: this.total.intValue > 0 ? this.total : currency(0),
        },
      },
    });
  };

  onChangeView = () => {
    this.setState((prevState) => ({ showGroups: !prevState.showGroups }));
  };

  renderGrid = () => {
    const { showGroups } = this.state;
    const groupsFilterableGrid = (
      <GroupsFilterableGrid
        onSelect={this.addEntryItem}
        canSelectGroup={!this.isRecord}
      />
    );
    const accountsFilterableGrid = (
      <AccountsFilterableGrid onSelect={this.addEntryItem} />
    );

    if (showGroups) {
      return groupsFilterableGrid;
    }

    return accountsFilterableGrid;
  };

  render() {
    const {
      periods,
      onClose,
      addUpdateJournalEntry,
      settings,
      choices,
      openModal,
    } = this.props;
    const { id, type } = this.state;
    const isNew = id === -1;

    if (!periods.length) {
      return (
        <div>
          <p>Cannot add journal entry if there are no periods defined.</p>
          <button onClick={onClose}>OK</button>
        </div>
      );
    }

    return (
      <div className="JournalEntryModal">
        <div className="left-half">
          <h2>
            {isNew ? 'New ' : ''}Journal Entry ({JournalLabel[type].full})
          </h2>
          <JournalEntryForm
            journalEntry={this.state}
            periods={periods}
            showConfirmation={this.showConfirmation}
            settings={settings}
            onUpdate={this.handleUpdate}
            choices={choices}
            openModal={openModal}
          />
          <div className="buttons">
            <button type="button" className="btn" onClick={onClose}>
              Cancel
            </button>
            <button
              type="button"
              className="btn"
              onClick={(e) => {
                addUpdateJournalEntry(this.state);
                onClose();
              }}
            >
              OK
            </button>
          </div>
        </div>
        <div className="right-half">
          <button type="button" className="btn" onClick={this.onChangeView}>
            Change View
          </button>
          {this.renderGrid()}
        </div>
      </div>
    );
  }
}

function mapStateToProps(state, { data }) {
  return {
    journalEntry: journalEntriesWithItemsSelector(state).find(
      (e) => e.id === data.journalEntryId
    ),
    periods: periodsSelector(state),
    settings: settingsSelector(state),
    choices: exchangeRateChoicesSelector(state),
  };
}

const mapDispatchToProps = {
  addUpdateJournalEntry,
  openModal,
};

export default connect(mapStateToProps, mapDispatchToProps)(JournalEntryModal);
